import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Slider } from '@atlas/components';
import { Bus, SiteConfigManager, useCasinoLastWinners } from '@atlas/core';
import { Loader } from '@atlas/ui';

import { LocalConfig } from 'common/utils';
import CasinoLastWinner from 'components/Casino/components/CasinoLastWinners/CasinoLastWinner';

const slidesPerView = 4;
const slidesToRender = slidesPerView + 1;

// this function is used to cut the queue of winners
// if the queue is bigger than queueLimit
// it will insert the new winners to the end of the queue
// and remove the outdated winners from the beginning of the queue
const queueLimit = 50;
const cutQueue = (prev, next) => {
  const mergeResult = [...prev, ...next];
  const overwhelmed = mergeResult.length > queueLimit;
  return mergeResult.slice(overwhelmed ? mergeResult.length - queueLimit : 0);
};

const CasinoLastWinners = () => {
  const isActive = SiteConfigManager.getConfig('config.config.lastWinners.desktop.isActive');
  const projectId = LocalConfig.get('projectId');
  const { lastWinners, loading } = useCasinoLastWinners(projectId);
  const [winnersToShow, setWinnersToShow] = useState([]);
  const [queue, setQueue] = useState([]);
  const isInsertingSlides = useRef(false);

  const onSlideChangeTransitionEnd = useCallback(
    swiper => {
      const startIndex = 0;
      const needToInsert =
        !isInsertingSlides.current &&
        queue.length > 0 &&
        swiper.realIndex % winnersToShow.length === startIndex;

      if (needToInsert) {
        const nextWinnersToShow = queue.slice(0, slidesToRender);
        const restQueue = queue.slice(slidesToRender);
        isInsertingSlides.current = true;
        setWinnersToShow([...winnersToShow, ...nextWinnersToShow]);
        setQueue(restQueue);
        // after inserting the new winners,the swiper should be slid
        // to the first slide without animation to avoid blinking
        swiper.slideTo(startIndex, 0, false);
        return;
      }

      const endIndex = winnersToShow.length - slidesPerView;
      const needToRemove =
        isInsertingSlides.current &&
        winnersToShow.length > slidesToRender &&
        swiper.realIndex % winnersToShow.length === endIndex;

      if (needToRemove) {
        const newWinnersToShow = winnersToShow.slice(winnersToShow.length - slidesToRender);
        isInsertingSlides.current = false;
        setWinnersToShow(newWinnersToShow);
        // after removing outdated winners, the swiper should be slid
        // to the first slide in the view
        // when the swiper is in the end position without animation to avoid blinking
        swiper.slideTo(slidesToRender - slidesPerView, 0, false);
      }
    },
    [winnersToShow, queue],
  );

  useEffect(() => {
    // if there are less slides than we need to render slider
    // then slides from the queue need to be added when they appear
    if (winnersToShow.length < slidesToRender && queue.length > 0) {
      const nextWinnersToShow = queue.slice(0, slidesToRender);
      const restQueue = queue.slice(slidesToRender);
      setWinnersToShow([...winnersToShow, ...nextWinnersToShow]);
      setQueue(restQueue);
    }
  }, [winnersToShow, queue]);

  useEffect(() => {
    const handler = ({ data }) => {
      setQueue(prev => cutQueue(prev, [data]));
    };

    Bus.on(Bus.events.casino.lastWinnerUpdate, handler);

    return () => Bus.off(Bus.events.casino.lastWinnerUpdate, handler);
  }, []);

  useEffect(() => {
    if (lastWinners.length === 0) {
      return;
    }

    setWinnersToShow(prev => [...prev, ...lastWinners.slice(0, slidesToRender)]);
    setQueue(prev => cutQueue(prev, lastWinners.slice(slidesToRender, lastWinners.length)));
  }, [lastWinners]);

  if (!isActive || winnersToShow.length === 0) {
    return null;
  }

  if (loading) {
    return <Loader className="casino__winners" />;
  }

  return (
    <div className="casino__winners">
      {winnersToShow.length >= slidesToRender ? (
        <Slider
          autoplay={{
            delay: 800,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          direction="vertical"
          onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
          slidesPerView={slidesPerView}
          spaceBetween={8}
        >
          {winnersToShow.map(winner => (
            <Slider.Slide key={`${crypto.randomUUID()}`}>
              <CasinoLastWinner lastWinner={winner} />
            </Slider.Slide>
          ))}
        </Slider>
      ) : (
        winnersToShow.map(winner => (
          <CasinoLastWinner key={`${crypto.randomUUID()}`} lastWinner={winner} />
        ))
      )}
    </div>
  );
};

export default CasinoLastWinners;
